import { overflow } from "@prismicio/editor-support/String"
import { SelectItem } from "@prismicio/editor-ui"

import { useRepositoryCustomTypes } from "../../../hooks/useRepositoryCustomTypes"
import { useRequestRepository } from "../../../hooks/useRequestRepository"
import { useAPIExplorer } from "../../APIExplorerContext"
import { RequestFormInput } from "./RequestFormInput"
import { RequestFormSelect } from "./RequestFormSelect"

type RequestTypeFieldsetProps = {
  nonRepeatableOnly?: boolean
}

/**
 * Display a fieldset managing a request document type setting.
 */
export function RequestTypeFieldset(props: RequestTypeFieldsetProps) {
  const { nonRepeatableOnly } = props
  const { config } = useAPIExplorer()

  return config.repository ? (
    <RequestTypeFielsetFromCustomTypesAPI
      nonRepeatableOnly={nonRepeatableOnly}
    />
  ) : (
    <RequestTypeFielsetFromDocumentAPI />
  )
}

// Sources custom types from the custom types API when the API Explorer lives
// inside a repository.
function RequestTypeFielsetFromCustomTypesAPI(props: RequestTypeFieldsetProps) {
  const { nonRepeatableOnly } = props

  const customTypes = useRepositoryCustomTypes(nonRepeatableOnly)

  return <RequestTypeFieldsetContent customTypes={customTypes} />
}

// Sources custom types from the document API when the API Explorer lives outside
// a repository. This is less precise than using the custom types API because we
// don't know if the type is repeatable or not.
function RequestTypeFielsetFromDocumentAPI() {
  const repository = useRequestRepository()

  // Converts custom types from Record<id, label> to Array<{ id, label }>
  const customTypes = Object.entries(repository?.types ?? {}).map(
    ([id, label]) => ({ id, label }),
  )

  return <RequestTypeFieldsetContent customTypes={customTypes} />
}

type RequestTypeFieldsetContentProps = {
  customTypes?: { id: string; label: string }[]
}

export function RequestTypeFieldsetContent(
  props: RequestTypeFieldsetContentProps,
) {
  const { customTypes } = props

  const { request } = useAPIExplorer()

  function setType(type: string) {
    request.setRequestOptions(request.type, {
      ...request.options[request.type],
      type,
    })
  }

  if (!customTypes || customTypes.length === 0) {
    return (
      <RequestFormInput
        value={request.options[request.type]?.["type"] ?? ""}
        onValueChange={setType}
        label="TYPE *"
        placeholder="page"
        error={!request.options[request.type]?.["type"]}
      />
    )
  }

  if (!request.options[request.type]?.["type"] && customTypes[0]) {
    setType(customTypes[0].id)
  }

  return (
    <RequestFormSelect
      label="TYPE *"
      value={request.options[request.type]?.["type"] ?? ""}
      onValueChange={setType}
      placeholder="page"
    >
      {customTypes.map(({ id }) => (
        <SelectItem key={id} value={id}>
          {overflow(id, 60)}
        </SelectItem>
      ))}
    </RequestFormSelect>
  )
}
